import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored auth on mount
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const updateUser = (newUser) => {
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  const login = async (login, password) => {
    const response = await fetch(`${window.API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (username, name, email, password) => {
    const response = await fetch(`${window.API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, name, email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Registration failed');
    }

    const data = await response.json();
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const resetPassword = async (newPassword) => {
    const response = await fetch(`${window.API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id, newPassword })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Password reset failed');
    }

    const data = await response.json();
    updateUser(data.user);
    return data;
  };

  const sendPhoneOTP = async (phone) => {
    const response = await fetch(`${window.API_URL}/api/auth/send-phone-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id, phone })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send OTP');
    }

    return response.json();
  };

  const verifyPhoneOTP = async (otp) => {
    const response = await fetch(`${window.API_URL}/api/auth/verify-phone-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id, otp })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Invalid OTP');
    }

    const data = await response.json();
    updateUser(data.user);
    return data;
  };

  const sendEmailOTP = async (email) => {
    const response = await fetch(`${window.API_URL}/api/auth/send-email-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id, email })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send OTP');
    }

    return response.json();
  };

  const verifyEmailOTP = async (otp) => {
    const response = await fetch(`${window.API_URL}/api/auth/verify-email-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id, otp })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Invalid OTP');
    }

    const data = await response.json();
    updateUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  // Skip phone or email verification (add later)
  const skipVerification = (type) => {
    if (type === 'phone') {
      updateUser({ ...user, phone_verified: true, phone_skipped: true });
    } else if (type === 'email') {
      updateUser({ ...user, email_verified: true, email_skipped: true });
    }
  };

  // Check if user needs to complete onboarding
  const needsOnboarding = user && (
    user.must_reset_password ||
    !user.phone_verified ||
    !user.email_verified
  );

  // Get current onboarding step
  const getOnboardingStep = () => {
    if (!user) return null;
    if (user.must_reset_password) return 'reset-password';
    if (!user.phone_verified) return 'phone-verify';
    if (!user.email_verified) return 'email-verify';
    return null;
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      needsOnboarding,
      getOnboardingStep,
      login,
      register,
      logout,
      updateUser,
      resetPassword,
      sendPhoneOTP,
      verifyPhoneOTP,
      sendEmailOTP,
      verifyEmailOTP,
      skipVerification
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
